import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { api, parseErrorDetail } from "../lib/api";

const CENTS = (c) => (c == null ? "—" : `$${(Math.abs(c) / 100).toFixed(2)}`);
const POLL_MS = 15000;

export default function OwnerOrders() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unseen, setUnseen] = useState(0);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [rowsR, unseenR] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/orders/unseen/count"),
      ]);
      setRows(rowsR.data || []);
      setUnseen(Number(unseenR.data?.unseen_count || 0));
      setError("");
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  return (
    <AdminShell title="Orders Inbox">
      <section className="apwo-panel" data-testid="owner-orders-panel">
        <div className="apwo-summary-head">
          <h2>
            Orders Inbox{" "}
            <span className="apwo-cell-muted">newest first</span>
            {unseen > 0 && (
              <span className="apwo-inbox-badge" data-testid="owner-inbox-unseen-badge">
                {unseen} new
              </span>
            )}
          </h2>
        </div>

        {loading && <p className="apwo-muted">Loading orders…</p>}
        {error && <div className="apwo-alert-error" data-testid="owner-orders-error">{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <p className="apwo-muted" data-testid="owner-orders-empty">No orders yet.</p>
        )}

        {rows.length > 0 && (
          <table className="apwo-summary-table" data-testid="owner-orders-table">
            <thead>
              <tr>
                <th></th>
                <th>Order #</th>
                <th>Submitted</th>
                <th>Customer</th>
                <th>Term</th>
                <th className="apwo-th-right">Items</th>
                <th className="apwo-th-right">Qty</th>
                <th className="apwo-th-right">Reward applied</th>
                <th className="apwo-th-right">Payable</th>
                <th className="apwo-th-right">Earned</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.order_id}
                  data-testid={`owner-orders-row-${r.order_number}`}
                  className={r.cancelled_at ? "apwo-row-cancelled"
                                              : (r.is_new_for_owner ? "apwo-row-new" : "")}
                >
                  <td>
                    {r.is_new_for_owner && !r.cancelled_at && (
                      <span className="apwo-badge apwo-badge--new" data-testid={`owner-orders-new-${r.order_number}`}>NEW</span>
                    )}
                    {r.cancelled_at && (
                      <span className="apwo-badge apwo-badge--danger" data-testid={`owner-orders-cancelled-${r.order_number}`}>Cancelled</span>
                    )}
                  </td>
                  <td><strong>{r.order_number}</strong></td>
                  <td className="apwo-cell-muted">{new Date(r.created_at).toLocaleString()}</td>
                  <td>{r.customer_display_name || r.customer_user_id}</td>
                  <td data-testid={`owner-orders-term-${r.order_number}`}>{r.payment_term || "—"}</td>
                  <td className="apwo-th-right">{r.line_count}</td>
                  <td className="apwo-th-right">{r.total_qty}</td>
                  <td className="apwo-th-right" data-testid={`owner-orders-reward-applied-${r.order_number}`}>
                    {r.reward_applied_cents > 0 ? `-${CENTS(r.reward_applied_cents)}` : "—"}
                  </td>
                  <td className="apwo-th-right">{CENTS(r.payable_total_cents)}</td>
                  <td className="apwo-th-right" data-testid={`owner-orders-earned-${r.order_number}`}>
                    {r.earned_reward_cents > 0 ? CENTS(r.earned_reward_cents) : "—"}
                  </td>
                  <td>
                    <span className="apwo-badge">{r.payment_status}</span>
                    {r.replaces_order_id && <div className="apwo-fineprint">replaces earlier</div>}
                    {r.replaced_by_order_id && <div className="apwo-fineprint">replaced by later</div>}
                  </td>
                  <td>
                    <button
                      className="apwo-linky"
                      onClick={() => nav(`/admin/orders/${r.order_id}`)}
                      data-testid={`owner-orders-view-${r.order_number}`}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </AdminShell>
  );
}
